import { AnnouncementsDetailsInput } from 'src/API';
import { createOrUpdateAnnouncementsDetails } from 'src/graphql/mutations';
import { listAnnouncementsDetails } from '../../graphql/queries';
import { AnnouncementDetails } from '../../types/announcements/announcements.types';
import { formatApiError } from '../../utils/errorUtils';
import { logger } from '../../utils/logger';
import { fetchGraphQLRequest } from './clients/graphqlClient';

/**
 * Fetches announcements from the GraphQL API
 * @returns Promise resolving to array of announcement details
 * @throws Error if API call fails
 */
export const fetchAnnouncementsAPI = async (): Promise<AnnouncementDetails[]> => {
  try {
    logger.info('Fetching announcements from GraphQL API');
    const result = await fetchGraphQLRequest<{ listAnnouncementsDetails: AnnouncementDetails[] }>({
      query: listAnnouncementsDetails,
      variables: {},
      requestName: 'listAnnouncementsDetails',
      requestType: 'query',
    });

    const announcements = result.listAnnouncementsDetails || [];
    logger.info(`Fetched ${announcements.length} announcements from API`);
    return announcements;
  } catch (error) {
    logger.error('Error fetching announcements from GraphQL API:', { error });
    throw formatApiError('Failed to fetch announcements', error);
  }
};

export const updateAnnouncementsAPI = async (
  mutationData: [AnnouncementsDetailsInput]
): Promise<{ success: boolean; message: string }> => {
  try {
    logger.info(`Updating announcements to GraphQL API: ${mutationData}`, mutationData);
    const result = await fetchGraphQLRequest<{
      createOrUpdateAnnouncementsDetails: { numberOfRecordsUpdated: number };
    }>({
      query: createOrUpdateAnnouncementsDetails,
      variables: { input: mutationData },
      requestName: 'createOrUpdateAnnouncementsDetails',
      requestType: 'mutation',
      auditConfig: { pageName: 'announcements' },
    });

    const numberOfRecordsUpdated = result.createOrUpdateAnnouncementsDetails.numberOfRecordsUpdated || 0;
    logger.info(`Announcements updated successfully`, { numberOfRecordsUpdated });
    return {
      success: numberOfRecordsUpdated > 0,
      message: `Announcements updated successfully (${numberOfRecordsUpdated} records updated)`,
    };
  } catch (error) {
    logger.error('Error updating announcements from GraphQL API:', { error });
    throw formatApiError('Failed to update announcements', error);
  }
};
